import * as React from "react";
import { RouteComponentProps } from "react-router";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { FrontDate } from "src/domain/model/frontDate";
import { IClientBookmarkRepository } from "src/domain/repository/client_bookmark_repository";
import { ICustomerRepository } from "src/domain/repository/customer_repository";
import {
  IJobCareerRepository,
  TJobCareerWithCustomerID,
} from "src/domain/repository/job_career_repository";
import { IApiClient, createApiClient } from "src/infra/api/client";
import { createClientBookmarkAPI } from "src/infra/api/service/client_bookmark_api";
import { createCustomerAPI } from "src/infra/api/service/customer_api";
import { createJobCareerAPI } from "src/infra/api/service/job_career_api";
import Progress from "src/presentation/common/organism/progress";
import ClientMarketingCampaignComponent from "src/presentation/component/client/campaign/client_marketing_campaign";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";

interface IProps extends RouteComponentProps<{}> {}

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const jobcareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const clientBookmarkRepository: IClientBookmarkRepository =
  createClientBookmarkAPI(apiClient);

const allCustomerIDs = (forProd: string[]): string[] => {
  if (process.env.REACT_APP_LIBERA_ENV === "dev") {
    return [
      "0kLSKmljg2M7djjX2PdcOC92G2K3",
      "2bOAImrai7Ow9pLmR8dAcgKxhRl1",
      "381LNvLckgQAZKGbnSNQrc0OxOG2",
      "3nyUvxmD3rggNXLM106Vg7Zfb9S2",
    ];
  } else if (process.env.REACT_APP_LIBERA_ENV === "stg") {
    return [
      "01gdJj3nBXYf70gYsmIODbqIV183",
      "022qmCQTTsbYwWzq1gtdTvYlrpF3",
      "02K5MlXQD1O1PHvxTSnBNe3uhtI2",
      "04qcOgQr9HcNi22SNGnKO5r9wGs2",
    ];
  } else if (process.env.REACT_APP_LIBERA_ENV === "prod") {
    return forProd;
  } else {
    return [];
  }
};

const someCustomerIDs = (forProd: string[]): string[] => {
  if (process.env.REACT_APP_LIBERA_ENV === "dev") {
    return [
      "0kLSKmljg2M7djjX2PdcOC92G2K3",
      "2bOAImrai7Ow9pLmR8dAcgKxhRl1",
      "381LNvLckgQAZKGbnSNQrc0OxOG2",
      "3nyUvxmD3rggNXLM106Vg7Zfb9S2",
    ].slice(0, forProd.length);
  } else if (process.env.REACT_APP_LIBERA_ENV === "stg") {
    return [
      "01gdJj3nBXYf70gYsmIODbqIV183",
      "022qmCQTTsbYwWzq1gtdTvYlrpF3",
      "02K5MlXQD1O1PHvxTSnBNe3uhtI2",
      "04qcOgQr9HcNi22SNGnKO5r9wGs2",
    ].slice(0, forProd.length);
  } else if (process.env.REACT_APP_LIBERA_ENV === "prod") {
    return forProd;
  } else {
    return [];
  }
};

export const ClientMarketingCampaignContainer: React.FC<IProps> = (props) => {
  const [cmoCustomers, setCmoCustomers] = React.useState<Customer[]>([]);
  const [toCMarketingCustomers, setToCMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [toBMarketingCustomers, setToBMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [webMarketingCustomers, setWebMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [snsMarketingCustomers, setSnsMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [contentMarketingCustomers, setContentMarketingCustomers] =
    React.useState<Customer[]>([]);
  const [seoCustomers, setSeoCustomers] = React.useState<Customer[]>([]);
  const [dataMarketingCustomers, setDataMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [eventMarketingCustomers, setEventMarketingCustomers] = React.useState<
    Customer[]
  >([]);
  const [tvcmMarketingCustomers, setTvcmMarketingCustomers] = React.useState<
    Customer[]
  >([]);

  const [bookmarks, setBookmarks] = React.useState<ClientBookmark[]>([]);
  const [jobCareers, setJobCareers] = React.useState<
    TJobCareerWithCustomerID[]
  >([]);

  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);

  React.useEffect(() => {
    try {
      clientBookmarkRepository.getAllByClient().then((items) => {
        setBookmarks(items);
      });

      jobcareerRepository
        .getAllByCustomerIDsForScout(
          allCustomerIDs([
            // ① CMO
            "sEzNHUSAeDMJuk1dhQ5hJwV22kQ2", // 足立 光
            "RbM24X1frzLoP8Ns2G0YOObm3562", // 堀本 修平
            "lTqiPV0tCjbvkhJUzEKM06CTEoJ3", // 宮脇啓輔
            "o3ZdotOyXTOYWx7zVNoSiThhb0K3", // 友貞ファラ洋輔
            // ② toC
            "WRMv8DXUW7deSAArXNWpkCjhwzt1", // 相澤 斗志輝
            "BHJA9woUXaMwFTnAFb2YQtS2UlI3", // 御子柴 正武
            // ② toB
            "WRd4zcHD7YVocpbKi25zTfmwG5S2", // タナカ ミノル
            "dTR7Lx0BtogxCG9Sfsm2DifQv6q1", // 高井伸
            // ③ Web広告
            "ZbWTFYmNx1es7znu9uToyfYiLYU2", // 中嶋大悟
            "ea0yXQ11bmbgBpSO4TnLL60aVRA2", // 真銅 正孝
            // ③ SNS
            "uq6owd9hpOWJqjzUFpxMZr2cz753", // 古川航輝
            "l4nLmzZ3LKVZ9nBtqCVjxSwkb4D3", // 米村 一精
            // ③ コンテンツ
            "UIZTJLkog0gKmc01ACQ7vkjYh3r1", // やまざき ひとみ
            "t7C3Oo02fHQIg74WzMbOJDMF8Yq2", // 影山由美子
            // ③ SEO
            "XiHvDQVvRNgIWF5fSBcwVJHMs6G2", // 炭田 一樹
            "dqn8vItPtmSxTZGbN9654hLg0yL2", // 大谷 琢
            // ④ データマーケティング
            "aaDeDklr73QMAEvR4Z8ZCgGwgxp1", // 森 学
            "GUoQSS9yiiPiQr5yn3a6NxmN3cE3", // 澤田 陽介
            "jn5AdTOG4Mdu6ieSqOMhTlmT8Rx2", // 松岡 貴英
            "xz64ZVPbUtUO3UtFqleSa7lMKyv2", // 小川 貴史
            // ⑤ 展示会
            "AfWPBsogQdPCJEn69TlUWSbqgOg1", // 菊地 伸行
            "lAtTUzObnRXJ90RdVpmjOPCEQyB2", // 東海林 学
            // ⑤ TVCM、広告
            "2GugZYV350V3QCoHguVrZoDqihA3", // 杉山 智哉
            "uaBeQ4aBZtPiUAWGsvLby5c2oQk2", // 大嶋慶
          ])
        )
        .then((items) => {
          setJobCareers(items);
        });

      // CMO
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "sEzNHUSAeDMJuk1dhQ5hJwV22kQ2", // 足立 光
            "RbM24X1frzLoP8Ns2G0YOObm3562", // 堀本 修平
            "lTqiPV0tCjbvkhJUzEKM06CTEoJ3", // 宮脇啓輔
            "o3ZdotOyXTOYWx7zVNoSiThhb0K3", // 友貞ファラ洋輔
          ])
        )
        .then((items) => {
          setCmoCustomers(items);
        });

      // ToC
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "WRMv8DXUW7deSAArXNWpkCjhwzt1", // 相澤 斗志輝
            "BHJA9woUXaMwFTnAFb2YQtS2UlI3", // 御子柴 正武
          ])
        )
        .then((items) => {
          setToCMarketingCustomers(items);
        });

      // ToB
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "WRd4zcHD7YVocpbKi25zTfmwG5S2", // タナカ ミノル
            "dTR7Lx0BtogxCG9Sfsm2DifQv6q1", // 高井伸
          ])
        )
        .then((items) => {
          setToBMarketingCustomers(items);
        });

      // webMarketing（Web広告）
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "ZbWTFYmNx1es7znu9uToyfYiLYU2", // 中嶋大悟
            "ea0yXQ11bmbgBpSO4TnLL60aVRA2", // 真銅 正孝
          ])
        )
        .then((items) => {
          setWebMarketingCustomers(items);
        });

      // snsMarketing
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "uq6owd9hpOWJqjzUFpxMZr2cz753", // 古川航輝
            "l4nLmzZ3LKVZ9nBtqCVjxSwkb4D3", // 米村 一精
          ])
        )
        .then((items) => {
          setSnsMarketingCustomers(items);
        });

      // contentMarketing
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "UIZTJLkog0gKmc01ACQ7vkjYh3r1", // やまざき ひとみ
            "t7C3Oo02fHQIg74WzMbOJDMF8Yq2", // 影山由美子
          ])
        )
        .then((items) => {
          setContentMarketingCustomers(items);
        });

      // seo
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "XiHvDQVvRNgIWF5fSBcwVJHMs6G2", // 炭田 一樹
            "dqn8vItPtmSxTZGbN9654hLg0yL2", // 大谷 琢
          ])
        )
        .then((items) => {
          setSeoCustomers(items);
        });

      // dataMarketing
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "aaDeDklr73QMAEvR4Z8ZCgGwgxp1", // 森 学
            "GUoQSS9yiiPiQr5yn3a6NxmN3cE3", // 澤田 陽介
            "jn5AdTOG4Mdu6ieSqOMhTlmT8Rx2", // 松岡 貴英
            "xz64ZVPbUtUO3UtFqleSa7lMKyv2", // 小川 貴史
          ])
        )
        .then((items) => {
          setDataMarketingCustomers(items);
        });

      // eventMarketing（展示会）
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "AfWPBsogQdPCJEn69TlUWSbqgOg1", // 菊地 伸行
            "lAtTUzObnRXJ90RdVpmjOPCEQyB2", // 東海林 学
          ])
        )
        .then((items) => {
          setEventMarketingCustomers(items);
        });

      // tvcMarketing（TVCM、広告）
      customerRepository
        .getAllByIDs(
          someCustomerIDs([
            "2GugZYV350V3QCoHguVrZoDqihA3", // 杉山 智哉
            "uaBeQ4aBZtPiUAWGsvLby5c2oQk2", // 大嶋慶
          ])
        )
        .then((items) => {
          setTvcmMarketingCustomers(items);
        });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  if (
    cmoCustomers.length === 0 ||
    toCMarketingCustomers.length === 0 ||
    toBMarketingCustomers.length === 0 ||
    webMarketingCustomers.length === 0 ||
    snsMarketingCustomers.length === 0 ||
    contentMarketingCustomers.length === 0 ||
    seoCustomers.length === 0 ||
    dataMarketingCustomers.length === 0 ||
    eventMarketingCustomers.length === 0 ||
    tvcmMarketingCustomers.length === 0 ||
    jobCareers.length === 0
  ) {
    return <Progress canAnimation={false} />;
  }

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}
      <ClientMarketingCampaignComponent
        isLoading={false}
        cmoCustomers={cmoCustomers}
        toCMarketingCustomers={toCMarketingCustomers}
        toBMarketingCustomers={toBMarketingCustomers}
        webMarketingCustomers={webMarketingCustomers}
        snsMarketingCustomers={snsMarketingCustomers}
        contentMarketingCustomers={contentMarketingCustomers}
        seoCustomers={seoCustomers}
        dataMarketingCustomers={dataMarketingCustomers}
        eventMarketingCustomers={eventMarketingCustomers}
        tvcmMarketingCustomers={tvcmMarketingCustomers}
        clientBookmarks={bookmarks}
        jobCareersWithCustomerID={jobCareers}
      />
    </React.Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/presentation/container/client_marketing_campaign.tsx